const NOMBRES: Record<string, string> = {
  registrado: 'Pagado',
  anulado: 'Anulado',
};

export function nombreDelEstadoDePago(estado: string): string {
  return NOMBRES[estado] ?? estado;
}

export function repartoPorConcepto(
  conceptos: { codigo: string; nombre: string; total: number }[] | undefined,
): [string, number][] {
  const lista = conceptos ?? [];
  if (!lista.some((c) => c.codigo !== 'tasa_mensual' && c.codigo !== 'interes')) {
    return [];
  }
  return lista.map((c) => [c.nombre, c.total]);
}

const ETIQUETAS: Record<string, string> = {
  sanitarios: 'Sanitarios',
  grandes_generadores: 'Servicios especiales',
  disposicion_final: 'Disposición final',
  aseo: 'Aseo',
  eventos: 'Evento',
  titulos: 'Art. 8',
};

const ORDEN_DE_LOS_CONCEPTOS = [
  'tasa_mensual',
  'sanitarios',
  'grandes_generadores',
  'disposicion_final',
  'aseo',
  'eventos',
  'titulos',
];

export interface GrupoDeConcepto<T> {
  concepto: string;
  nombre: string;
  facturas: T[];
}

/** Cada concepto con sus facturas, en el orden en que llegan: de la más vieja a la más nueva. */
export function agruparPorConcepto<T extends { concepto: string; conceptoNombre: string }>(
  facturas: T[],
): GrupoDeConcepto<T>[] {
  const grupos = new Map<string, GrupoDeConcepto<T>>();
  for (const f of facturas) {
    const grupo = grupos.get(f.concepto) ?? {
      concepto: f.concepto,
      nombre: f.conceptoNombre,
      facturas: [],
    };
    grupo.facturas.push(f);
    grupos.set(f.concepto, grupo);
  }
  const puesto = (concepto: string) => {
    const i = ORDEN_DE_LOS_CONCEPTOS.indexOf(concepto);
    return i < 0 ? ORDEN_DE_LOS_CONCEPTOS.length : i;
  };
  return [...grupos.values()].sort((a, b) => puesto(a.concepto) - puesto(b.concepto));
}

export interface GrupoDeCuenta<T> {
  /** Vacío para las facturas que no son de una sola cuenta: las de varios predios. */
  numeroCuenta: string;
  direccion: string | null;
  facturas: T[];
}

/**
 * Las facturas de un concepto, cuenta por cuenta: la cuenta uno con su enero, febrero..., luego
 * la dos. Lo pidió EPAGAL: con tres predios la lista intercalaba los tres eneros, los tres
 * febreros, y la cajera no veía qué debía cada predio. Dentro de cada cuenta sigue el orden en
 * que llegan, de la más vieja a la más nueva, y las cuentas van por su factura más vieja.
 */
export function agruparPorCuenta<
  T extends { numeroCuenta: string | null; direccionServicio: string | null },
>(facturas: T[]): GrupoDeCuenta<T>[] {
  const grupos = new Map<string, GrupoDeCuenta<T>>();
  for (const f of facturas) {
    const clave = f.numeroCuenta ?? '';
    const grupo = grupos.get(clave) ?? {
      numeroCuenta: clave,
      direccion: f.direccionServicio,
      facturas: [],
    };
    grupo.facturas.push(f);
    grupos.set(clave, grupo);
  }
  return [...grupos.values()];
}

export function etiquetaDelConcepto(codigo: string | undefined): string | null {
  return codigo ? (ETIQUETAS[codigo] ?? null) : null;
}
